import { decodeCreationOptions, decodeRequestOptions, encodeCredential } from './webauthn.ts';

export interface SoraneConfig {
  siteId: string;
  endpoint?: string;
  title?: string;
  lead?: string;
}

export class SoraneApi {
  private readonly base: string;

  constructor(private readonly config: SoraneConfig) {
    this.base = (config.endpoint ?? window.location.origin).replace(/\/$/, '');
  }

  private url(path: string): string {
    return `${this.base}/api/v1/sites/${this.config.siteId}${path}`;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(this.url(path), {
      credentials: 'include',
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
    const json = (await res.json()) as T & {
      error?: string;
      error_description?: string;
      errors?: { field: string; message: string }[];
    };
    if (!res.ok) {
      if (json.error === 'validation_failed' && json.errors?.length) {
        const first = json.errors[0]!;
        throw new Error(first.message);
      }
      throw new Error(json.error_description ?? json.error ?? res.statusText);
    }
    return json;
  }

  async getSession(): Promise<SessionInfo | null> {
    try {
      return await this.request<SessionInfo>('/auth/session');
    } catch {
      return null;
    }
  }

  async getMemberConduct(): Promise<string> {
    const res = await this.request<{ conduct: string }>('/member/conduct');
    return res.conduct;
  }

  async suggestPresetDisplayName(): Promise<string> {
    const res = await this.request<{ displayName: string }>('/auth/onboarding/slug');
    return res.displayName;
  }

  async completeOnboarding(input: {
    birthDate: string;
    displayName: string;
    conductAccepted: boolean;
  }): Promise<SessionInfo> {
    return this.request('/auth/onboarding', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async login(): Promise<void> {
    const begin = await this.request<{ handle: string; publicKey: PublicKeyCredentialRequestOptions }>(
      '/auth/login/options',
      { method: 'POST', body: JSON.stringify({ role: 'inquirer' }) },
    );
    const cred = await navigator.credentials.get({
      publicKey: decodeRequestOptions(begin.publicKey),
    });
    if (!cred) throw new PasskeyCancelledError();
    await this.request('/auth/login/verify', {
      method: 'POST',
      body: JSON.stringify({ handle: begin.handle, clientResponse: encodeCredential(cred) }),
    });
  }

  async logout(): Promise<void> {
    await this.request('/auth/logout', { method: 'POST', body: '{}' });
  }

  async register(): Promise<void> {
    const begin = await this.request<{ handle: string; publicKey: PublicKeyCredentialCreationOptions }>(
      '/auth/register/options',
      { method: 'POST', body: '{}' },
    );
    const cred = await navigator.credentials.create({
      publicKey: decodeCreationOptions(begin.publicKey),
    });
    if (!cred) throw new PasskeyCancelledError();
    await this.request('/auth/register/verify', {
      method: 'POST',
      body: JSON.stringify({ handle: begin.handle, clientResponse: encodeCredential(cred) }),
    });
  }

  /** Login with an existing Passkey, or register when none is available on this device. */
  async continueWithPasskey(): Promise<'login' | 'register'> {
    try {
      await this.login();
      return 'login';
    } catch (err) {
      if (isPasskeyCancelled(err)) throw err;
      if (!shouldRegisterPasskey(err)) throw err;
      await this.register();
      return 'register';
    }
  }

  async listThreads(): Promise<ThreadRow[]> {
    const res = await this.request<{ threads: ThreadRow[] }>('/threads');
    return res.threads;
  }

  async getThread(id: string): Promise<{ thread: ThreadRow; messages: MessageRow[] }> {
    return this.request(`/threads/${encodeURIComponent(id)}`);
  }

  async createThread(question: string): Promise<{ moderation?: 'pending' }> {
    return this.request('/threads', {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
  }
}

function isPasskeyCancelled(err: unknown): boolean {
  if (err instanceof PasskeyCancelledError) return true;
  if (err instanceof DOMException && err.name === 'NotAllowedError') return true;
  return false;
}

function shouldRegisterPasskey(err: unknown): boolean {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('authentication_failed') || msg.includes('invalid_or_expired_ceremony')) {
      return true;
    }
  }
  if (err instanceof DOMException && err.name === 'NotFoundError') return true;
  return false;
}

export class PasskeyCancelledError extends Error {
  constructor() {
    super('Passkey cancelled');
    this.name = 'PasskeyCancelledError';
  }
}

export interface SessionInfo {
  role: string;
  displayName?: string;
  displaySlug?: string;
  onboardingComplete?: boolean;
}

export interface ThreadRow {
  id: string;
  ref: string;
  kind: string;
  status: string;
  subject: string | null;
  canonicalUrl: string | null;
  urlTitle: string | null;
  updatedAt: string;
}

export interface MessageRow {
  id: string;
  authorRole: string;
  body: string;
  createdAt: string;
  agentCredentialId?: string | null;
  agentName?: string | null;
  model?: string | null;
  inquirerDisplayName?: string | null;
}
